import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";

/**
 * Floating WhatsApp CTA. Replaces the previous "Made with" badge.
 * Shows a friendly "Have a question? Contact us" bubble tied to a
 * WhatsApp click-to-chat link. Hidden if no WhatsApp number is configured.
 * Number and default message are read from /api/settings.
 */
export default function FloatingWhatsApp() {
  const { t } = useLang();
  const [number, setNumber] = useState("");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        const map = Object.fromEntries(r.data.map((s) => [s.key, s.value]));
        setNumber((map.whatsapp_number || "").replace(/[^\d+]/g, ""));
        setMessage(map.whatsapp_message || "");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!number) return;
    const tm = setTimeout(() => setOpen(true), 1200);
    const hide = setTimeout(() => setOpen(false), 9000);
    return () => {
      clearTimeout(tm);
      clearTimeout(hide);
    };
  }, [number]);

  if (!number) return null;

  const href = `https://wa.me/${number.replace(/^\+/, "")}?text=${encodeURIComponent(
    message
  )}`;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-2"
      data-testid="floating-whatsapp-wrap"
    >
      {open && (
        <div
          className="relative max-w-[240px] rounded-2xl bg-white shadow-xl border border-slate-200 px-4 py-3 text-slate-800"
          data-testid="floating-whatsapp-bubble"
        >
          <button
            aria-label="close"
            onClick={() => setOpen(false)}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-slate-800 text-white text-xs leading-none flex items-center justify-center shadow"
            data-testid="floating-whatsapp-close"
          >
            ×
          </button>
          <div className="text-sm font-semibold">{t("wa.have_question")}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t("wa.contact_us")}</div>
          <div className="absolute -bottom-2 right-6 w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45" />
        </div>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setOpen(false)}
        aria-label="WhatsApp"
        className="relative flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea855] text-white pl-3 pr-4 py-3 rounded-full shadow-lg shadow-green-800/30 hover:scale-105 transition"
        data-testid="floating-whatsapp"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="22"
          height="22"
          fill="white"
          className="relative"
        >
          <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .18 5.32.18 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 0 0 5.72 1.46h.01c6.56 0 11.88-5.32 11.88-11.88 0-3.17-1.23-6.15-3.43-8.44ZM12.07 21.3h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.76.98 1-3.66-.22-.37a9.42 9.42 0 0 1-1.44-5c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.98 6.67 2.77a9.36 9.36 0 0 1 2.77 6.67c0 5.2-4.24 9.44-9.45 9.44Zm5.19-7.07c-.28-.14-1.68-.83-1.94-.92-.26-.1-.45-.14-.64.14-.19.28-.74.92-.9 1.11-.17.19-.33.21-.61.07-.28-.14-1.2-.44-2.28-1.41-.84-.75-1.41-1.68-1.58-1.96-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.5-.07-.14-.64-1.54-.88-2.11-.23-.55-.47-.48-.64-.49-.16-.01-.35-.01-.53-.01a1 1 0 0 0-.74.35c-.25.28-.97.95-.97 2.31s.99 2.68 1.13 2.87c.14.19 1.95 2.98 4.72 4.18.66.28 1.17.45 1.57.58.66.21 1.26.18 1.73.11.53-.08 1.68-.69 1.92-1.36.24-.66.24-1.24.17-1.36-.07-.12-.26-.19-.54-.33Z" />
        </svg>
        <span className="relative font-semibold text-sm hidden sm:inline">
          {t("wa.contact_us")}
        </span>
      </a>
    </div>
  );
}
